<template>
  <div class="image-input">
    <input
      type="file"
      accept="image/png, image/jpeg, image/heic"
      ref="imgInputRef"
      @change="changeImage"
      style="display: none"
    />

    <div class="fake-input" @click="triggerFileInput">
      <font-awesome-icon class="fake-input__icon" icon="paperclip" />
      <span
        class="fake-input__filename"
        :class="{ 'fake-input__filename--uploaded': filename }"
      >
        <template v-if="filename">{{ filename }}</template>
        <template v-else>Selecciona arxiu...</template>
      </span>
    </div>

    <p v-if="orientacio">Orientació EXIF: {{ orientacio }}</p>
    <div class="image-preview" v-if="base64img">
      <h4>Previsualització</h4>
      <img :src="base64img" style="max-width: 100%; height: auto" />
      <div class="image-preview__buttons">
        <button @click.stop="rotateLeft">↺ Girar esquerra</button>
        <button @click.stop="rotateRight">↻ Girar dreta</button>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref } from "vue";

export default defineComponent({
  props: {
    idVetlla: {
      type: Number,
      required: true,
    },
  },
  emits: ["update:base64Foto"],
  setup(props, { emit }) {
    const fotoFile = ref<File | null>(null);
    const imgInputRef = ref<HTMLInputElement | null>(null);
    const filename = ref<string | null>(null);
    const base64img = ref<string>("");
    const originalBase64 = ref<string>("");
    const orientacio = ref<string>();
    const rotation = ref(0);
    const missatge = ref("");
    const signatura = ref("");
    const isVisible = ref(true);

    const triggerFileInput = () => {
      imgInputRef.value?.click();
    };

    const blobToDataUrl = (blob: File) =>
      new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });

    const rotateImage = (): Promise<string> =>
      new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => {
          const w = img.width;
          const h = img.height;
          const canvas = document.createElement("canvas");
          const ctx = canvas.getContext("2d");
          if (!ctx) return reject("No canvas context");

          const angleRad = (rotation.value * Math.PI) / 180;

          if (rotation.value % 180 === 0) {
            canvas.width = w;
            canvas.height = h;
          } else {
            canvas.width = h;
            canvas.height = w;
          }

          ctx.save();
          ctx.translate(canvas.width / 2, canvas.height / 2);
          ctx.rotate(angleRad);
          ctx.drawImage(img, -w / 2, -h / 2);
          ctx.restore();

          const rotatedBase64 = canvas.toDataURL("image/jpeg", 0.95);
          const cleanBase64 = rotatedBase64.replace(
            /^data:image\/jpeg/,
            "data:image/png",
          );

          base64img.value = cleanBase64;
          originalBase64.value = cleanBase64;
          resolve(cleanBase64);
        };
        img.onerror = reject;
        img.src = originalBase64.value;
      });

    const rotateLeft = () => {
      rotation.value = (rotation.value - 90 + 360) % 360;
      rotateImage().then((cleanBase64) => {
        emit("update:base64Foto", cleanBase64);
      });
    };

    const rotateRight = () => {
      rotation.value = (rotation.value + 90) % 360;
      rotateImage().then((cleanBase64) => {
        emit("update:base64Foto", cleanBase64);
      });
    };

    const changeImage = async (e: Event) => {
      const files = (e.target as HTMLInputElement).files;
      if (!files || !files[0]) {
        fotoFile.value = null;
        filename.value = null;
        return;
      }

      const file = files[0];
      filename.value = file.name;
      fotoFile.value = file;

      const base64 = await blobToDataUrl(file);
      originalBase64.value = base64;
      base64img.value = base64;

      emit("update:base64Foto", base64);
    };

    const uploadImage = async (extraData: {
      missatge: string;
      signatura: string;
      isVisible: boolean;
    }) => {
      if (!fotoFile.value) throw new Error("No hi ha cap fitxer seleccionat");

      const form = new FormData();
      form.append("nivell_rotacio", rotation.value.toString());
      form.append("id_vetlla", props.idVetlla.toString());
      form.append("missatge", extraData.missatge);
      form.append("signatura", extraData.signatura);
      form.append("is_visible", extraData.isVisible.toString());
      form.append("foto", fotoFile.value);

      const res = await fetch("/api/diapositiva/create", {
        method: "POST",
        body: form,
      });

      if (!res.ok) {
        const text = await res.text();
        console.error("Error resposta:", text);
        throw new Error(text);
      }
    };

    return {
      imgInputRef,
      changeImage,
      uploadImage,
      filename,
      orientacio,
      base64img,
      originalBase64,
      rotateLeft,
      rotateRight,
      rotateImage,
      missatge,
      signatura,
      isVisible,
      triggerFileInput,
    };
  },
});
</script>

<style lang="scss">
.image-input {
  cursor: pointer;
}

.fake-input {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 0.6em;

  &__filename {
    padding: $input-padding;
    height: $input-height;

    border-radius: $input-border-radius;
    border: $input-border;
    flex: 1;

    display: flex;
    flex-direction: row;
    align-items: center;

    color: #696969;

    &--uploaded {
      color: $text-color;
    }
  }

  &__icon {
    font-size: 1.5rem;
  }
}
.image-preview__buttons {
  margin-top: 0.5rem;
  display: flex;
  gap: 1rem;

  button {
    padding: 0.5em 1em;
    background: #f0f0f0;
    border: 1px solid #ccc;
    border-radius: 4px;
    cursor: pointer;

    &:hover {
      background: #ddd;
    }
  }
}

.debug {
  white-space: pre-wrap;
  font-family: monospace;
  font-size: 0.9rem;
  margin-top: 1rem;
  background: #f6f8fa;
  padding: 1rem;
  border-radius: 4px;
}
</style>
